import React, { useCallback, useEffect, useMemo, useState } from "react";
import BREContactsTable from "./BREContactsTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { isCancel } from "../../../services/api/axios";
import normalizeJobTitle from "../../../utils/jobTitleNormalizer";
import useRowSelection from "../../../hooks/useRowSelection";
import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import { contactProfileConfig } from "../../../shared/profile-configs/ContactProfileConfig";
import ContactsModuleSwitcher from "../../../shared/components/ContactsModuleSwitcher";

import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";
import BrandPagination from "../../../layouts/Pagination";

import {
  Box,
  Button,
  Card,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useAuth } from "../../../providers/AuthProvider";
import SearchBar from "../../../shared/components/SearchBar";
import { downloadBlobFile } from "./BREContactsHelpers";
import {
  getUniqueCompanyNames,
  getUniqueJobTitles,
  sortContactsByColumn,
} from "../../../utils/contactListUtils";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

function BREContacts({
  embedded = false,
  companyId: forcedCompanyId,
  refreshKey = 0,
}) {
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);

  const [profileOpen, setProfileOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");

  const [page, setPage] = useState(1);
  const limit = 50;

  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [searchParams] = useSearchParams();
  const companyId = forcedCompanyId ?? searchParams.get("company_id");

  const navigate = useNavigate();
  const { token } = useAuth();
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const offset = useMemo(() => (page - 1) * limit, [page, limit]);

  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    togglePage,
    clear: clearSelection,
    setSelectedIds,
  } = useRowSelection(contacts, (contact) => contact.id);

  // ===========================
  // Fetch Contacts
  // ===========================
  const fetchContacts = useCallback(
    async (signal) => {
      try {
        const res = await api.get("/user-contacts/", {
          signal,
          params: {
            limit,
            offset,
            company_id: companyId || undefined,
            search: searchTerm || undefined,
          },
        });

        if (Array.isArray(res.data)) {
          setContacts(res.data);
          setTotalContacts(res.data.length);
        } else {
          setContacts(res.data?.data ?? []);
          setTotalContacts(res.data?.total ?? 0);
        }
        setSelectedIds(new Set());

        return res;
      } catch (err) {
        if (isCancel(err)) return null;

        setSnackbarSeverity("error");
        setSnackbarMsg("Failed to fetch contacts. Please try again.");
        setSnackbarOpen(true);
        return null;
      }
    },
    [companyId, offset, searchTerm, limit, setSelectedIds],
  );

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchContacts(controller.signal);
    return () => controller.abort();
  }, [token, fetchContacts, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, jobTitleFilter, companyFilter, companyId]);

  // ===========================
  // Filters + Sorting
  // ===========================
  const uniqueJobTitles = useMemo(
    () => getUniqueJobTitles(contacts, normalizeJobTitle),
    [contacts],
  );

  const uniqueCompanyNames = useMemo(
    () => getUniqueCompanyNames(contacts),
    [contacts],
  );

  const filteredByQuickFilters = useMemo(() => {
    return contacts.filter((c) => {
      const companyPass = !companyFilter
        ? true
        : (c.company?.name || "")
            .toLowerCase()
            .includes(companyFilter.toLowerCase());

      const jobPass = !jobTitleFilter
        ? true
        : normalizeJobTitle(c.job_title) === jobTitleFilter;

      return companyPass && jobPass;
    });
  }, [contacts, companyFilter, jobTitleFilter]);

  const sortedVisibleContacts = useMemo(() => {
    return sortContactsByColumn({
      contacts: filteredByQuickFilters,
      orderBy,
      orderDirection,
      getValue: (contact, key) => {
        switch (key) {
          case "name":
            return contact.name;
          case "company":
            return contact.company?.name;
          case "job_title":
            return contact.job_title;
          default:
            return "";
        }
      },
    });
  }, [filteredByQuickFilters, orderBy, orderDirection]);

  const handleSortRequest = useCallback(
    (key) => {
      const isAsc = orderBy === key && orderDirection === "asc";
      setOrderBy(key);
      setOrderDirection(isAsc ? "desc" : "asc");
    },
    [orderBy, orderDirection],
  );

  const hasContacts = sortedVisibleContacts.length > 0;

  // ===========================
  // Export
  // ===========================
  const handleExportContacts = () => {
    api
      .get("/user-contacts/export", { responseType: "blob" })
      .then((response) => {
        const cd = response.headers?.["content-disposition"];
        const match = cd && /filename="?([^"]+)"?/.exec(cd);
        downloadBlobFile(response.data, match?.[1] || "UserContacts.xlsx");
      })
      .catch(() => {
        setSnackbarSeverity("error");
        setSnackbarMsg("Export failed. Please try again.");
        setSnackbarOpen(true);
      });
  };

  const handleOpenActionsMenu = useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  const handleExportContactsFromMenu = () => {
    handleCloseActionsMenu();
    handleExportContacts();
  };

  // ===========================
  // Delete
  // ===========================
  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      setSnackbarSeverity("info");
      setSnackbarMsg("No rows selected.");
      setSnackbarOpen(true);
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = async () => {
    const ids = Array.from(selectedIds);

    setDeleting(true);
    const results = await Promise.allSettled(
      ids.map((id) => api.delete(`/user-contacts/${id}/`)),
    );

    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;

    setSnackbarSeverity(failed ? "warning" : "success");
    setSnackbarMsg(
      failed
        ? `Deleted ${succeeded}, but ${failed} failed.`
        : `Deleted ${succeeded} contact${succeeded === 1 ? "" : "s"}.`,
    );

    setSnackbarOpen(true);
    setDeleting(false);
    setDeleteConfirmOpen(false);

    await fetchContacts();
    setSelectedIds(new Set());
  };

  const handleClearSelectionFromMenu = () => {
    handleCloseActionsMenu();
    clearSelection();
  };

  const handleDeleteSelectedFromMenu = () => {
    handleCloseActionsMenu();
    handleDeleteSelected();
  };

  // ===========================
  // RENDER
  // ===========================
  if (embedded) {
    return (
      <>
        <BREContactsTable
          hasContacts={hasContacts}
          embedded={embedded}
          selectedIds={selectedIds}
          selectedCount={selectedCount}
          allSelected={allSelected}
          someSelected={someSelected}
          toggleOne={toggleOne}
          togglePage={togglePage}
          navigate={navigate}
          sortedVisibleContacts={sortedVisibleContacts}
          orderBy={orderBy}
          orderDirection={orderDirection}
          onSort={handleSortRequest}
          companyFilter={companyFilter}
          onCompanyFilterChange={setCompanyFilter}
          uniqueCompanyNames={uniqueCompanyNames}
          jobTitleFilter={jobTitleFilter}
          onJobTitleFilterChange={setJobTitleFilter}
          uniqueJobTitles={uniqueJobTitles}
          setActiveContact={setActiveContact}
          setProfileOpen={setProfileOpen}
        />
        {hasContacts && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <BrandPagination
              page={page}
              count={Math.ceil(totalContacts / limit)}
              onChange={(_e, val) => setPage(val)}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <PeopleIcon sx={{ fontSize: 19 }} />
            </Box>
            <ContactsModuleSwitcher current="bre" />
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search Contact Name, Company or Job Title..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate("/bre-contacts/new");
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <PersonAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add BR&amp;E Contact</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportContactsFromMenu}>
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleClearSelectionFromMenu}>
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDeleteSelectedFromMenu}>
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Selected</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Card sx={CARD_SX}>
          <BREContactsTable
            hasContacts={hasContacts}
            embedded={embedded}
            selectedIds={selectedIds}
            selectedCount={selectedCount}
            allSelected={allSelected}
            someSelected={someSelected}
            toggleOne={toggleOne}
            togglePage={togglePage}
            navigate={navigate}
            sortedVisibleContacts={sortedVisibleContacts}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={handleSortRequest}
            companyFilter={companyFilter}
            onCompanyFilterChange={setCompanyFilter}
            uniqueCompanyNames={uniqueCompanyNames}
            jobTitleFilter={jobTitleFilter}
            onJobTitleFilterChange={setJobTitleFilter}
            uniqueJobTitles={uniqueJobTitles}
            setActiveContact={setActiveContact}
            setProfileOpen={setProfileOpen}
          />
        </Card>
        {hasContacts && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <BrandPagination
              page={page}
              count={Math.ceil(totalContacts / limit)}
              onChange={(_e, val) => setPage(val)}
            />
          </Box>
        )}
      </Box>

      <EntityProfile
        open={profileOpen}
        entity={activeContact}
        sections={activeContact ? contactProfileConfig(activeContact) : []}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
        onClose={() => setProfileOpen(false)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Contacts"
        description={`Are you sure you want to delete ${selectedIds.size} contact${selectedIds.size === 1 ? "" : "s"}? This action can't be undone.`}
      />
    </Box>
  );
}

export default BREContacts;
